import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMapPin, FiArrowRight, FiSkipForward } from 'react-icons/fi';
import { updateUserProfile, updateDerasar } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { StateCitySelect } from '../../components/common';
import styles from './OnboardingPage.module.css';

const REQUIRED_ADDRESS_FIELDS = [
  ['flatNo', 'Flat / Bunglow No.'],
  ['buildingName', 'Apartment / Bunglow Name'],
  ['addressLine1', 'Address Line 1'],
  ['city', 'City'],
  ['state', 'State'],
  ['pincode', 'Pincode'],
];

const OnboardingPage = () => {
  const { user, activeDerasarId, markProfileCompleted } = useAuth();
  const navigate = useNavigate();

  // For a new admin whose derasar is still pending (not yet approved),
  // activeDerasarId is null. Resolve derasar ID directly from user.derasars.
  const adminDerasarId = activeDerasarId
    || (user?.role === 'derasar_admin' && user?.derasars?.[0]
        ? (user.derasars[0].derasarId?.id || user.derasars[0].derasarId)
        : null);
  const [form, setForm] = useState({
    flatNo: '', buildingName: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', country: 'India',
    notes: '',
    establishDate: '', derasarNotes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const missingField = REQUIRED_ADDRESS_FIELDS.find(([key]) => !String(form[key] || '').trim());
    if (missingField) {
      setError(`${missingField[1]} is required before continuing.`);
      return;
    }
    setLoading(true);
    try {
      if (user?.role === 'derasar_admin' && adminDerasarId) {
        await updateDerasar(adminDerasarId, { establishDate: form.establishDate, notes: form.derasarNotes });
      }
      await updateUserProfile(form);
      markProfileCompleted();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      // Save empty profile to mark as completed so this screen won't appear again
      await updateUserProfile(form);
      markProfileCompleted();
    } catch (_) {
      // Even if save fails, mark locally so we don't block the user
      markProfileCompleted();
    } finally {
      navigate('/dashboard');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.iconBox}><FiMapPin size={22} /></div>
          <div>
            <h2 className={styles.title}>Welcome, {user?.name?.split(' ')[0]}!</h2>
            <p className={styles.subtitle}>Help us personalise your experience by adding a few details.</p>
          </div>
        </div>

        <p className={styles.note}>
          Flat / Bunglow No., Apartment / Bunglow Name, Address Line 1, City, State, and Pincode are required before joining a Derasar.
          You can skip this for now and add them later from your profile.
        </p>

        {error && <div className={styles.errorMsg}>{error}</div>}

        <form onSubmit={handleSave}>
          <h4 style={{ marginBottom: 16 }}>{user?.role === 'derasar_admin' ? 'Derasar Address' : 'Address Details'}</h4>

          <div className={styles.fieldRow}>
            <div className="form-group">
              <label>Flat / Bunglow No. *</label>
              <input name="flatNo" value={form.flatNo} onChange={handleChange} placeholder="e.g. A-101" />
            </div>
            <div className="form-group">
              <label>Apartment / Bunglow Name *</label>
              <input name="buildingName" value={form.buildingName} onChange={handleChange} placeholder="e.g. Shanti Niketan" />
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className="form-group">
              <label>Address Line 1 *</label>
              <input name="addressLine1" value={form.addressLine1} onChange={handleChange} placeholder="Street / Area / Locality" />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input name="addressLine2" value={form.addressLine2} onChange={handleChange} placeholder="Street / Area / Locality" />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <StateCitySelect
              state={form.state}
              city={form.city}
              onStateChange={(val) => setForm(f => ({ ...f, state: val, city: '' }))}
              onCityChange={(val) => setForm(f => ({ ...f, city: val }))}
              required
            />
          </div>

          <div className={styles.fieldRow}>
            <div className="form-group">
              <label>Pincode *</label>
              <input name="pincode" value={form.pincode} onChange={handleChange} placeholder="e.g. 380001" />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input name="country" value={form.country} onChange={handleChange} placeholder="India" />
            </div>
          </div>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--color-border)' }} />

          {user?.role === 'derasar_admin' ? (
            <>
              <h4 style={{ marginBottom: 16 }}>Derasar Details</h4>
              <div className={styles.fieldRow}>
                <div className="form-group">
                  <label>Establish Date</label>
                  <input type="date" name="establishDate" value={form.establishDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Admin Notes</label>
                  <textarea
                    name="derasarNotes"
                    value={form.derasarNotes}
                    onChange={handleChange}
                    placeholder="Internal notes for this Derasar…"
                    rows={2}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <h4 style={{ marginBottom: 16 }}>Personal Details</h4>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Any additional notes about yourself…"
                  rows={2}
                />
              </div>
            </>
          )}

          <div className={styles.actions}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <FiArrowRight size={15} />
              {loading ? 'Saving…' : 'Save & Continue'}
            </button>
            <button type="button" className={styles.skipBtn} onClick={handleSkip} disabled={loading}>
              <FiSkipForward size={14} /> Skip for now
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardingPage;
